import React, { useMemo, useState, useEffect, useCallback, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlyceLoading,
  ProductSidebar,
  ProductSidebarHeader,
  ProductSidebarTitle,
  Merchant,
  Product,
  MerchantHeader,
  ProductDetailsWrapper,
  IProductSidebarProps,
  ProductTabs,
} from '@alycecom/ui';
import { upperFirstLetter } from '@alycecom/utils';
import { useMeasure } from 'react-use';

import { getIsLoading, getProduct } from '../../store/product.selectors';
import { loadProductDetailsRequest, resetProductDetails } from '../../store/product.actions';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import ProductSubmitForm from '../ProductSubmitForm/ProductSubmitForm';

import useStyles from './productDetails.styles';

export interface IProductDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (options: string, optionsData?: Record<string, string>) => void;
  hashId: string;
  gl?: string;
  productId?: number;
  optionsData?: Record<string, string>;
  disabledContinue?: boolean;
  sidebarProps?: Partial<IProductSidebarProps>;
}

const ProductDetails = ({
  isOpen,
  hashId,
  gl = 'gift',
  productId,
  optionsData,
  onClose,
  disabledContinue = false,
  sidebarProps = {},
  onSubmit = () => {},
}: IProductDetailsSidebarProps): ReactElement => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const [isMerchantSidebar, setIsMerchantSidebar] = useState(false);

  const isLoading = useSelector(getIsLoading);
  const productDetails = useSelector(getProduct);

  const handleToggleMerchant = useCallback(() => {
    trackEvent('Product - Opened merchant info', { product: productDetails }, {});
    setIsMerchantSidebar(isMerchantView => !isMerchantView);
  }, [productDetails, trackEvent]);

  const handleToggleProductTab = useCallback(
    (tab: ProductTabs) => {
      trackEvent('Product - Clicked tab', { tab });
    },
    [trackEvent],
  );

  const sidebarHeader = useMemo(() => {
    if (!productDetails) {
      return <></>;
    }
    const headerComponent = isMerchantSidebar ? (
      <MerchantHeader title="Merchant details" logo={productDetails?.provider.logo} onBack={handleToggleMerchant} />
    ) : (
      <ProductSidebarTitle title={`${upperFirstLetter(gl)} details`} />
    );
    return (
      <ProductSidebarHeader onClose={onClose} overrideClasses={classes}>
        {headerComponent}
      </ProductSidebarHeader>
    );
  }, [handleToggleMerchant, productDetails, isMerchantSidebar, classes, gl, onClose]);

  useEffect(() => {
    if (isOpen && productId) {
      dispatch(loadProductDetailsRequest({ hashId, productId }));
    } else {
      dispatch(resetProductDetails());
      setIsMerchantSidebar(false);
    }
  }, [isOpen, hashId, productId, dispatch]);

  const [boxMarginBottom, setBoxMarginBottom] = useState(5);
  const [optionsRef, { height }] = useMeasure();
  useEffect(() => {
    if (height) {
      const mb = Math.floor((height + 64) / 8);
      setBoxMarginBottom(mb);
    }
  }, [height]);

  return (
    <ProductSidebar isOpen={isOpen} onClose={onClose} header={sidebarHeader} {...sidebarProps}>
      <AlyceLoading isLoading={isLoading} boxProps={{ mt: 6 }}>
        <ProductDetailsWrapper hasProduct={!!productDetails}>
          {isMerchantSidebar ? (
            <Merchant product={productDetails} />
          ) : (
            <Product
              product={productDetails}
              onShowMerchant={handleToggleMerchant}
              onToggleTab={handleToggleProductTab}
              wrapperProps={{ mb: boxMarginBottom }}
              overrideClasses={classes}
            >
              <ProductSubmitForm
                ref={optionsRef as React.Ref<HTMLDivElement>}
                product={productDetails}
                optionsData={optionsData}
                submitText={`Select this ${gl} and continue`}
                disabledContinue={disabledContinue}
                onSubmit={onSubmit}
              />
            </Product>
          )}
        </ProductDetailsWrapper>
      </AlyceLoading>
    </ProductSidebar>
  );
};

export default ProductDetails;
